// Cours premium n°6 — "Valider • Consolider • Être autonome • Se préparer à l'examen"
// Circulation A2 — 14 chapitres (sans visuels, trame identique aux cours 1/2/3/4/5)
// Progression : Cours 1 : je découvre → Cours 2 : j'observe et anticipe → Cours 3 : je construis
// → Cours 4 : je conduis seul → Cours 5 : je consolide → Cours 6 : je valide (12 h minimales)

export const cours = {
  slug: 'sixieme-circulation-a2',
  title: 'Ma 6ᵉ circulation A2',
  subtitle: 'Valider • Consolider • Être autonome • Se préparer à l\'examen',
  duree: '2 heures de conduite',
  mantra: 'OBSERVER → ANALYSER → ANTICIPER → DÉCIDER → AGIR → SÉCURISER',
  intro: "Nous arrivons théoriquement aux 12 heures minimales de circulation si les six sessions ont toutes duré 2 heures. Mais atteindre le minimum réglementaire ne signifie pas automatiquement être prêt pour l'examen : ton niveau réel reste déterminant. Cette séance n'ajoute presque plus de nouvelles notions. Elle sert à observer que tu es capable de mobiliser l'ensemble de tes compétences sans assistance permanente, puis à déterminer précisément ce qu'il reste éventuellement à travailler avant présentation à l'examen.",
  sections: [
    {
      num: '01',
      kicker: 'Contrat final',
      title: 'Ton bilan n°5 détermine ta dernière séance',
      lead: "Avant de partir, nous reprenons ton dernier bilan. Mais cette fois, l'objectif est différent. Nous ne cherchons plus principalement à construire une nouvelle compétence.",
      type: 'timeline',
      steps: [
        { time: 'Forts', label: 'TES POINTS FORTS', desc: 'Les compétences sur lesquelles tu peux t\'appuyer avec confiance.' },
        { time: 'Fragile', label: 'TES POINTS ENCORE FRAGILES', desc: 'Les situations qui demandent encore de la vigilance.' },
        { time: 'Cible', label: 'TON OBJECTIF PRIORITAIRE', desc: 'Ce que tu veux valider pendant ces 2 dernières heures.' },
      ],
      retenir: 'NOUS VÉRIFIONS QUE TU SAIS MOBILISER TOUTES TES COMPÉTENCES ENSEMBLE.',
    },
    {
      num: '02',
      kicker: 'Prise en main',
      title: 'Tu prends en charge ta conduite dès le départ',
      lead: "Dès les premiers mètres, tu prends tes décisions. La radio sert essentiellement à l'itinéraire, à l'organisation du groupe, à la pédagogie lorsque cela est nécessaire, et naturellement à la sécurité.",
      type: 'list',
      list: [
        { label: 'INSTALLATION', desc: 'Réglages, posture, prise de contact avec la moto.' },
        { label: 'ÉQUIPEMENT', desc: 'Vérifié seul, sans avoir à être rappelé.' },
        { label: 'CONTRÔLES', desc: 'Rétros, angles morts, arrière : intégrés au démarrage.' },
        { label: 'DÉMARRAGE', desc: 'Progressif, contrôlé, communication anticipée.' },
        { label: 'INSERTION', desc: 'Décidée seul, sans attendre une consigne extérieure.' },
        { label: 'PREMIÈRES INTERSECTIONS', desc: 'Traitées avec la même exigence qu\'en fin de séance.' },
      ],
      retenir: 'TU N\'ATTENDS PLUS LA CONSIGNE POUR AGIR.',
    },
    {
      num: '03',
      kicker: 'Conduite complète',
      title: 'Tu montres une conduite complète',
      lead: "Nous ne travaillons plus séparément le regard, l'allure, le positionnement, les contrôles, les distances, l'anticipation… Tout doit maintenant fonctionner ensemble, sans que tu aies besoin de reconstruire consciemment chaque étape.",
      type: 'cards3',
      cards: [
        { title: 'OBSERVER & ANALYSER', desc: 'Tu vas chercher l\'information et tu l\'interprètes en continu.' },
        { title: 'ANTICIPER & DÉCIDER', desc: 'Tu prépares ta réponse avant que la situation ne l\'impose.' },
        { title: 'AGIR & SÉCURISER', desc: 'Tu exécutes avec précision, tout en conservant tes marges.' },
      ],
      retenir: 'OBSERVER → ANALYSER → ANTICIPER → DÉCIDER → AGIR → SÉCURISER.',
    },
    {
      num: '04',
      kicker: 'Environnements variés',
      title: 'Tu conduis dans des environnements variés',
      lead: "Le parcours doit être suffisamment riche pour observer ta capacité d'adaptation. L'objectif n'est pas d'accumuler les difficultés : il est d'observer que ta conduite s'adapte lorsque l'environnement change.",
      type: 'list',
      list: [
        { label: 'AGGLOMÉRATION', desc: 'Usagers vulnérables, priorités, allures faibles.' },
        { label: 'INTERSECTIONS', desc: 'Priorités variées, positionnement, communication.' },
        { label: 'GIRATOIRES', desc: 'Sortie anticipée, contrôles, engagement propre.' },
        { label: 'HORS AGGLOMÉRATION', desc: 'Allures supérieures, distances plus grandes.' },
        { label: 'VIRAGES', desc: 'Trajectoire, allure d\'entrée, regard porté loin.' },
        { label: 'CIRCULATION DENSE', desc: 'Espaces réduits, communication renforcée.' },
        { label: 'CHANGEMENTS DE VOIE', desc: 'Contrôles complets, anticipation, exécution nette.' },
        { label: 'INSERTIONS', desc: 'Fluides, sans forcer et sans hésiter.' },
        { label: 'VOIE RAPIDE', desc: 'Lorsque l\'environnement le permet, en flux plus rapide.' },
      ],
      retenir: 'TA CONDUITE S\'ADAPTE LORSQUE L\'ENVIRONNEMENT CHANGE.',
    },
    {
      num: '05',
      kicker: 'Lecture avancée',
      title: 'Tu lis la route avant qu\'elle ne t\'impose une réaction',
      lead: "Ton regard doit désormais travailler loin devant. Tu ne te demandes plus seulement « que dois-je faire maintenant ? », mais « qu'est-ce qui peut se passer ensuite ? »",
      type: 'list',
      list: [
        { label: 'RALENTISSEMENT', desc: 'Détecté au feu stop, à l\'allure d\'un autre véhicule.' },
        { label: 'INTERSECTION', desc: 'Repérée en amont, priorités préparées.' },
        { label: 'VÉHICULE STATIONNÉ', desc: 'Un piéton, une portière, un déboîtement peuvent surgir.' },
        { label: 'MASQUE DE VISIBILITÉ', desc: 'Camion, virage, végétation : tu réduis la marge d\'incertitude.' },
        { label: 'PIÉTON', desc: 'Sur le trottoir, en approche : tu adaptes tôt ton allure.' },
        { label: 'CHANGEMENT DE LIMITATION', desc: 'Anticipé grâce à la signalisation en amont.' },
        { label: 'VIRAGE', desc: 'Trajectoire, allure et regard travaillés avant l\'entrée.' },
        { label: 'MODIFICATION DU TRAFIC', desc: 'Densité qui augmente, files qui se forment.' },
      ],
      retenir: 'QU\'EST-CE QUI PEUT SE PASSER ENSUITE ?',
    },
    {
      num: '06',
      kicker: 'Marges maintenues',
      title: 'Tu gères tes marges de sécurité',
      lead: "C'est l'un des fils conducteurs de toute la formation. Plus la situation devient incertaine, plus tu dois chercher à augmenter ta marge d'action.",
      type: 'list',
      list: [
        { label: 'TEMPS', desc: 'Tu te donnes le temps d\'observer, d\'analyser, de décider.' },
        { label: 'ESPACE', desc: 'Devant, derrière, sur les côtés — préservé en continu.' },
        { label: 'VISIBILITÉ', desc: 'Tu te places pour voir et pour être vu.' },
        { label: 'FREINAGE POSSIBLE', desc: 'Tu restes capable de ralentir à tout moment sans à-coup.' },
        { label: 'ÉVITEMENT POSSIBLE', desc: 'Ton positionnement laisse une porte de sortie.' },
        { label: 'POSSIBILITÉ DE RENONCER', desc: 'Si la situation se dégrade, tu peux encore ne pas t\'engager.' },
      ],
      retenir: 'UNE BONNE CONDUITE TE LAISSE TOUJOURS UNE SOLUTION.',
    },
    {
      num: '07',
      kicker: 'Décider sans précipiter',
      title: 'Tu prends des décisions sans te précipiter',
      lead: "Passer ou attendre ? S'insérer ou laisser passer ? Dépasser ou rester derrière ? Changer de voie ou conserver ta position ? Tu analyses avant d'agir. Et surtout, tu sais faire quelque chose de fondamental : renoncer. Une opportunité n'est jamais une obligation.",
      type: 'cards3',
      cards: [
        { title: 'PASSER OU ATTENDRE ?', desc: 'Si l\'espace ou la visibilité manque, je préserve ma marge.' },
        { title: 'DÉPASSER OU RESTER ?', desc: 'Seulement quand la visibilité et la loi l\'autorisent réellement.' },
        { title: 'RENONCER', desc: 'Pas un échec : un signe de motard autonome et lucide.' },
      ],
      retenir: 'UNE OPPORTUNITÉ N\'EST JAMAIS UNE OBLIGATION.',
    },
    {
      num: '08',
      kicker: 'Imprévu maîtrisé',
      title: 'Tu gères l\'imprévu',
      lead: "La circulation réelle ne suit jamais parfaitement le scénario prévu. Un véhicule change de direction. Un piéton apparaît. Un véhicule freine. Une visibilité disparaît. Une voie devient indisponible. Tu dois montrer que tu sais réagir dans l'ordre, sans panique et sans précipitation.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'DÉTECTER', desc: 'Tu prends l\'information sans dramatiser.' },
        { time: '2', label: 'RALENTIR SI BESOIN', desc: 'Tu gagnes du temps sans freiner brutalement.' },
        { time: '3', label: 'CRÉER DE LA MARGE', desc: 'Tu retrouves de l\'espace pour choisir.' },
        { time: '4', label: 'ANALYSER', desc: 'Tu comprends ce qui se passe réellement.' },
        { time: '5', label: 'DÉCIDER', desc: 'Tu choisis la réponse la plus sûre.' },
        { time: '6', label: 'AGIR', desc: 'Tu passes à l\'action de manière progressive et contrôlée.' },
      ],
      retenir: 'SANS PANIQUE. SANS PRÉCIPITATION.',
    },
    {
      num: '09',
      kicker: 'Cohabitation',
      title: 'Tu conduis avec les autres',
      lead: "Tu n'es jamais seul sur la route. Ton objectif est constant : ne pas gêner, ne pas surprendre, ne pas être surpris.",
      type: 'list',
      list: [
        { label: 'PIÉTONS', desc: 'Priorité, visibilité, patience.' },
        { label: 'CYCLISTES & EDPM', desc: 'Distance latérale, dépassement anticipé.' },
        { label: 'DEUX-ROUES MOTORISÉS', desc: 'Trajectoires parfois imprévisibles : tu leur laisses de l\'espace.' },
        { label: 'VOITURES', desc: 'Tu restes lisible, tu communiques tôt, tu ne surprends pas.' },
        { label: 'POIDS LOURDS', desc: 'Angles morts importants : tu te rends visible et tu attends.' },
        { label: 'TRANSPORTS EN COMMUN', desc: 'Arrêts, redémarrages, voies dédiées : tu t\'adaptes.' },
        { label: 'AUTRES MOTOS', desc: 'Tu partages la voie sans imposer ta trajectoire.' },
      ],
      retenir: 'NE PAS GÊNER. NE PAS SURPRENDRE. NE PAS ÊTRE SURPRIS.',
    },
    {
      num: '10',
      kicker: 'Phase d\'autonomie',
      title: 'Tu effectues une vraie phase d\'autonomie',
      lead: "Ton enseignant t'indique une direction (par exemple : « Suis maintenant la direction Grenoble »), puis il intervient le moins possible. Tu dois simultanément chercher la signalisation, suivre ton itinéraire, observer la circulation, respecter les règles, anticiper et décider. Et si tu te trompes, tu continues en sécurité.",
      type: 'cards3',
      cards: [
        { title: 'JE CHERCHE LA SIGNALISATION', desc: 'Sans quitter la route des yeux plus qu\'un instant.' },
        { title: 'JE SUIS L\'ITINÉRAIRE', desc: 'Mais ma priorité absolue reste la conduite.' },
        { title: 'JE CONTINUE EN SÉCURITÉ', desc: 'Une erreur d\'itinéraire se corrige. Une décision dangereuse, non.' },
      ],
      body: "Cette compétence correspond directement à la phase d'autonomie d'environ 5 minutes prévue lors de l'épreuve circulation A2.",
      retenir: 'L\'ITINÉRAIRE RESTE SECONDAIRE PAR RAPPORT À LA SÉCURITÉ.',
    },
    {
      num: '11',
      kicker: 'Régularité',
      title: 'Tu maintiens ton niveau dans la durée',
      lead: "C'est probablement l'un des points les plus importants de cette dernière séance. Une bonne décision isolée ne suffit plus. Nous cherchons la régularité, sur plusieurs dizaines de minutes.",
      type: 'list',
      list: [
        { label: 'OBSERVATION', desc: 'Toujours active, même après une heure de conduite.' },
        { label: 'ALLURE', desc: 'Adaptée en permanence, sans à-coups ni relâchement.' },
        { label: 'CONTRÔLES', desc: 'Efficaces et intégrés au rythme naturel de la conduite.' },
        { label: 'DISTANCES', desc: 'Maintenues même quand le trafic évolue.' },
        { label: 'DÉCISIONS COHÉRENTES', desc: 'Justes et prises au bon moment, tout au long du trajet.' },
        { label: 'COMMUNICATION', desc: 'Claire et anticipée jusqu\'à la fin de la séance.' },
        { label: 'CONCENTRATION', desc: 'Stable, y compris lorsque la fatigue arrive.' },
      ],
      retenir: 'LE NIVEAU ATTENDU DOIT DEVENIR TON NIVEAU HABITUEL.',
    },
    {
      num: '12',
      kicker: 'Conditions d\'évaluation',
      title: 'Tu effectues une séquence proche des conditions d\'évaluation',
      lead: "Une séquence suffisamment longue pendant laquelle l'enseignant intervient très peu. Pas pour te « piéger », pas pour te mettre artificiellement la pression, mais pour répondre à une question simple : que se passe-t-il quand personne ne t'aide ?",
      type: 'list',
      list: [
        { label: 'PRISE D\'INFORMATION', desc: 'Regard actif, indices exploités, contrôles pertinents.' },
        { label: 'ALLURE', desc: 'Adaptée au contexte, aux limitations, à la visibilité.' },
        { label: 'RÉGLEMENTATION', desc: 'Signalisation, priorités, règles respectées.' },
        { label: 'COMMUNICATION', desc: 'Clignotants, position, regards, feux stop clairs.' },
        { label: 'POSITIONNEMENT', desc: 'Choisi pour préparer visiblement l\'intention.' },
        { label: 'ESPACES DE SÉCURITÉ', desc: 'Distances devant, derrière et latérales préservées.' },
        { label: 'ANALYSE', desc: 'Tu lis la situation, tu identifies les risques.' },
        { label: 'ADAPTATION', desc: 'Tu ajustes ta conduite en continu.' },
        { label: 'AUTONOMIE', desc: 'Tu conduis seul, sans avoir besoin d\'être guidé.' },
        { label: 'COURTOISIE', desc: 'Tu facilites la circulation, tu ne l\'imposes pas.' },
      ],
      retenir: 'QUE SE PASSE-T-IL LORSQUE JE NE T\'AIDE PLUS ?',
    },
    {
      num: '13',
      kicker: 'Auto-bilan',
      title: 'Tu fais ton propre bilan avant celui de ton enseignant',
      lead: "À l'arrêt, il est essentiel de te laisser parler en premier. Tu réponds à quatre questions. Ensuite seulement, ton enseignant apporte son analyse. Nous comparons ton ressenti et la réalité observée : c'est extrêmement intéressant pédagogiquement.",
      type: 'list',
      list: [
        { label: '« QU\'EST-CE QUE J\'AI BIEN MAÎTRISÉ ? »', desc: 'Tu identifies ce qui a été concrètement solide.' },
        { label: '« QUELLE SITUATION M\'A POSÉ PROBLÈME ? »', desc: 'Tu nommes le moment délicat, sans le fuir.' },
        { label: '« QUELLE DÉCISION AURAIS-JE PU AMÉLIORER ? »', desc: 'Tu proposes toi-même l\'ajustement.' },
        { label: '« EST-CE QUE JE ME SENS CAPABLE DE CONDUIRE SANS ÊTRE GUIDÉ ? »', desc: 'Tu poses honnêtement ton propre diagnostic.' },
      ],
      retenir: 'TON RESSENTI ↔ LA RÉALITÉ OBSERVÉE : LA COMPARAISON EST PRÉCIEUSE.',
    },
    {
      num: '14',
      kicker: 'Bilan final',
      title: 'Ton bilan final : où en es-tu ?',
      lead: "Cette dernière grille peut devenir la plus importante de ton livret. Tu t'auto-évalues sur les compétences réellement observées lors de l'épreuve A2, avec trois niveaux :",
      type: 'bilan',
      questions: [
        'Observer loin et largement ?',
        'Détecter suffisamment tôt les risques ?',
        'Adapter mon allure seul ?',
        'Choisir mon positionnement ?',
        'Appliquer la réglementation ?',
        'Gérer intersections et giratoires ?',
        'Maintenir mes distances de sécurité ?',
        'Gérer changements de voie et insertions ?',
        'Dépasser uniquement lorsque les conditions sont réunies ?',
        'Négocier les virages en sécurité ?',
        'M\'adapter aux différents environnements ?',
        'Prendre en compte les usagers vulnérables ?',
        'Communiquer clairement ?',
        'Suivre une direction en autonomie ?',
        'Gérer un imprévu ?',
        'Renoncer lorsque la situation l\'exige ?',
        'Récupérer après une erreur ?',
        'Maintenir ma concentration et mon niveau dans la durée ?',
        'Expliquer et justifier mes décisions ?',
      ],
      blocks: [
        { title: '🟢 AUTONOME', color: 'green' },
        { title: '🟠 À CONSOLIDER', color: 'amber' },
        { title: '🔴 À TRAVAILLER', color: 'red' },
      ],
      body: "Deux chemins possibles à l'issue de cette séance. 🟢 Compétences suffisamment stables : je poursuis ma préparation vers l'examen. 🟠🔴 Compétences encore fragiles : nous définissons ensemble les séances complémentaires nécessaires et leurs objectifs précis. 12 heures de circulation constituent un minimum réglementaire, pas une garantie automatique de niveau suffisant.",
      retenir: 'LE BUT N\'EST PAS DE CONDUIRE SANS TON ENSEIGNANT. LE BUT EST DE NE PLUS AVOIR BESOIN DE LUI POUR PRENDRE UNE DÉCISION SÛRE.',
    },
  ],
  // Récap final de la progression complète (affiché en pied de cours)
  progression: [
    { session: 'SESSION 1', label: 'Je découvre et j\'applique.' },
    { session: 'SESSION 2', label: 'J\'observe, j\'anticipe et je décide.' },
    { session: 'SESSION 3', label: 'Je planifie et je construis ma conduite.' },
    { session: 'SESSION 4', label: 'Je conduis avec davantage d\'autonomie.' },
    { session: 'SESSION 5', label: 'Je consolide et je recherche la régularité.' },
    { session: 'SESSION 6', label: 'Je démontre que je peux conduire seul, en sécurité.' },
  ],
};
